import { createClient } from 'contentful'

const client = createClient({
  space: process.env.VUE_APP_CONTENTFUL_SPACE_ID,
  environment: process.env.VUE_APP_CONTENTFUL_ENVIRONMENT,
  accessToken: process.env.VUE_APP_CONTENTFUL_ACCESS_TOKEN
})

export default {
  namespaced: true,
  state: {
    categories: [],
    products: [],
    tags: [],
    tagsParents: [],
    blogPosts: [],
    requests: [],
    events: [],
    fullScreenGalleries: [],
    singlePages: [],
    partners: Object,
    stores: [],
    root: Object,
    nodes: [],
    postsCategories: [],
    realizationsCategories: [],
    realizations: []
  },
  getters: {
    categories: state => state.categories,
    category: state => slug => state.categories.find(category => category.fields.slug === slug),
    products: state => state.products,
    product: state => slug => state.products.find(product => product.fields.slug === slug),
    tags: state => state.tags,
    tagsParents: state => state.tagsParents,
    tag: state => slug => state.tags.find(tag => tag.fields.slug === slug),
    blogPosts: state => state.blogPosts,
    blogPost: state => slug => state.blogPosts.find(blogPost => blogPost.fields.slug === slug),
    requests: state => state.requests,
    events: state => state.events,
    fullScreenGalleries: state => state.fullScreenGalleries,
    singlePages: state => state.singlePages,
    singlePage: state => slug => state.singlePages.find(singlePage => singlePage.fields.slug === slug),
    partners: state => state.partners,
    stores: state => state.stores,
    root: state => state.root,
    nodes: state => state.nodes,
    postsCategories: state => state.postsCategories,
    realizationsCategories: state => state.realizationsCategories,
    realizations: state => state.realizations
  },
  mutations: {
    updateCategories (state, categories) {
      state.categories = categories
    },
    updateProducts (state, products) {
      state.products = products
    },
    updateTags (state, tags) {
      state.tags = tags
    },
    updateTagsParents (state, tagsParents) {
      state.tagsParents = tagsParents
    },
    updateBlogPosts (state, blogPosts) {
      state.blogPosts = blogPosts
    },
    updateRequests (state, requests) {
      state.requests = requests
    },
    updateEvents (state, events) {
      state.events = events
    },
    updateFullScreenGalleries (state, fullScreenGalleries) {
      state.fullScreenGalleries = fullScreenGalleries
    },
    updateSinglePages (state, singlePages) {
      state.singlePages = singlePages
    },
    updatePartners (state, partner) {
      state.partners = partner
    },
    updateStores (state, stores) {
      state.stores = stores
    },
    updateRoot (state, root) {
      state.root = root
    },
    updateNodes (state, nodes) {
      state.nodes = nodes
    },
    updatePostsCategories (state, nodes) {
      state.postsCategories = nodes
    },
    updateRealizationsCategories (state, nodes) {
      state.realizationsCategories = nodes
    },
    updateRealizations (state, nodes) {
      state.realizations = nodes
    }
  },
  actions: {
    async loadCategories ({ commit, rootState }) {
      const entries = await client.getEntries({
        content_type: 'category',
        locale: 'it'
      })

      commit('updateCategories', entries.items)
    },
    async loadProducts ({ commit, rootState }) {
      const entries = await client.getEntries({
        content_type: 'products',
        locale: 'it',
        limit: 900
      })

      commit('updateProducts', entries.items)
    },
    async loadTags ({ commit, rootState }) {
      const entries = await client.getEntries({
        content_type: 'tags',
        locale: 'it',
        order: 'fields.title'
      })

      commit('updateTags', entries.items)
    },
    async loadTagsParents ({ commit, rootState }) {
      const entries = await client.getEntries({
        content_type: 'tagsParent',
        locale: 'it'
      })

      commit('updateTagsParents', entries.items)
    },
    async loadBlogPosts ({ commit, rootState }) {
      const entries = await client.getEntries({
        content_type: 'posts',
        locale: 'it'
      })

      commit('updateBlogPosts', entries.items)
    },
    async loadRequests ({ commit, rootState }) {
      const requests = await client.getEntries({
        content_type: 'requests',
        locale: 'it'
      })

      commit('updateRequests', requests.items)
    },
    async loadEvents ({ commit, rootState }) {
      const entries = await client.getEntries({
        content_type: 'events',
        locale: 'it'
      })

      commit('updateEvents', entries.items)
    },
    async loadFullScreenGalleries ({ commit, rootState }) {
      const entries = await client.getEntries({
        content_type: 'fullScreenGallery',
        locale: 'it',
        order: 'fields.order'
      })

      commit('updateFullScreenGalleries', entries.items)
    },
    async loadSinglePages ({ commit, rootState }) {
      const entries = await client.getEntries({
        content_type: 'singlePages',
        locale: 'it',
        include: 10
      })
      commit('updateSinglePages', entries.items)
    },
    async loadStores ({ commit, rootState }) {
      const entries = await client.getEntries({
        content_type: 'stores',
        locale: 'it',
        order: 'fields.order'
      })
      commit('updateStores', entries.items)
    },
    async loadPartners ({ commit, rootState }) {
      const partners = await client.getEntry('7x6S0Ren3uSwd2Udc0TO47', {
        locale: 'it'
      })
      commit('updatePartners', partners)
    },
    async loadRoot ({ commit, rootState }) {
      const root = await client.getEntry('7JBWTWGTBw454cKpn988Jk', {
        locale: 'it',
        include: 10
      })
      commit('updateRoot', root)
    },
    async loadNodes ({ commit, rootState }) {
      const entries = await client.getEntries({
        content_type: 'nodes',
        include: 10,
        locale: 'it'
      })

      commit('updateNodes', entries.items)
    },
    async loadPostsCategories ({ commit, rootState }) {
      const entries = await client.getEntries({
        content_type: 'postsCategories',
        locale: 'it',
        order: 'fields.order',
        limit: 20
      })

      commit('updatePostsCategories', entries.items)
    },
    async loadRealizationsCategories ({ commit, rootState }) {
      const entries = await client.getEntries({
        content_type: 'realizationsCategories',
        locale: 'it',
        order: 'fields.order',
        limit: 20,
        include: 10
      })

      commit('updateRealizationsCategories', entries.items)
    },
    async loadRealizations ({ commit, rootState }) {
      const entries = await client.getEntries({
        content_type: 'realizations',
        locale: 'it',
        limit: 100,
        include: 10
      })

      commit('updateRealizations', entries.items)
    }
  }
}
